import * as THREE from 'three';
import { lightShare } from '../../sim/light';
import { hash3 } from '../fishSwim';
import { WATER_FOG, noFog, type FogUniforms } from '../skin';
import type { HourSun } from '../sunlight';
import { SHAFTS } from './shafts';

/**
 * The Reef's water's particles (M173, the user's brief, `docs/AQUARIUM_PARTICLES.md`: "a living, atmospheric Hawaiian
 * reef", "make sunlight-catching particles the signature effect, rather than bubbles"). Three batches of points, each one
 * draw, moved on the GPU from a fixed draw of each particle and a clock, nothing allocated as they go:
 *
 * - **The suspended**: the plankton, the organic flecks, the detritus and a little fine sediment in the water all round
 *   the camera, in world space: each its own place in one of three shells about the camera (`layers`), so the near ones
 *   cross the view faster than the far (the brief's "multiple depth layers to create parallax"); drifting with the reef's
 *   current, each turning on its own slow eddy; drawn as a square of whole pixels, 1 to 3 across by its size and
 *   distance, its opacity carrying what a pixel cannot; lit by the light at its own depth on the day's clarity and the
 *   hour's sun, more in the shafts' light and looking toward the sun (forward scattering); a few catching the sun
 *   briefly (`glint`); fewer and darker by night; faded at its shell's edge, near the camera, in the water's veil and as
 *   the camera draws back from the reef. Their number by the day's simulated plankton.
 * - **The sediment**: small clouds of fine grains where a parrotfish bites the reef (`SEDIMENT`), the fish's own bouts
 *   (`FishSchools`), the coarse grains settling faster than the fine, the cloud spreading and fading.
 * - **The bubbles**: rare small groups just under the surface, rising faster the larger, the larger wobbling, gone at
 *   the surface (`BUBBLES`).
 *
 * Drawn after the rock, the coral and the shafts and before the animals (`renderOrder` 3.3), testing the depth the floor
 * and the rock and the coral's feet write and writing none: hidden behind the reef, never over a fish.
 */

/**
 * Every setting the brief asks for (its 12, "Expose configurable settings for"), a rendering choice each (JUDGED unless
 * said; "Start with visually restrained defaults"):
 *
 * - `density`: all the suspended particles' number, times (and `quality`'s); `sizeTimes` their drawn sizes'; `drift`
 *   their eddies' pace; `current` the current's part in their drift; `turbulence` their eddies' reach, m.
 * - `layers`: each shell's radius about the camera, m, how many of its particles to a cubic meter at the plankton's
 *   reference (`planktonRef`, in proportion to it between `planktonSpan`), and their drawn sizes, m (drawn larger than
 *   the water's real specks so they are seen at all from the camera's 2.4 to 16 m, as the fish are drawn larger,
 *   `REEF_SCENE.fish.drawnTimes`); the farther the sparser, its particles the larger. `most` at most, all shells.
 * - `opacity`: a particle's opacity in full light, before its pixels' share it covers, its fades and its veil take theirs
 *   (above 1, so the farthest, covering a fraction of a pixel, are seen at all); `nightLight` the light it is seen by on a moonless night, against
 *   noon's; `forward` how much brighter one is looking toward the sun (forward scattering); `inShaft` how much brighter
 *   in a shaft at its brightest; `maxPx` the most pixels across one is drawn, a nearer one fading as it would grow past.
 * - `glint`: the share of particles that catch the sun, how often each does (`everyS`), how sharp (`sharp`, the power of
 *   its sine), how bright (`intensity`), the power of the light at its depth it goes by (`depthPower`: little sparkle
 *   deep); `warmBelowDeg` the sun's height under which its light warms toward gold (the morning's and evening's).
 * - `fade`: from what share of its shell's radius a particle fades out (`fromShare`), and the near camera's (`nearM`).
 * - `byDepth`: its visibility by its depth, m, times, between the given depths (the brief's 6: "Particle visibility by
 *   depth"), on top of its light's.
 * - `zoomOut`: the share of its opacity lost as the camera draws back from the reef, from `nearM` to `farM` from it (the
 *   brief's 9: "When zoomed out, particles should become less visually prominent").
 * - `quality`: a computer's and a phone's share of the particles; `adapt` the frame's seconds over which for a second
 *   they are drawn fewer (`slowS`, under 25 a second: a screen held at 30, as a phone saving its battery is, never
 *   counted slow, M173's review) and under which for five seconds more again (`quickS`, over 28 a second: so a screen at
 *   30 wins back a share a slow moment cut, M173's second review), how fast, and the fewest share kept; `easeS` the seconds the number shown and the draw-back's fade ease over, so neither jumps.
 */
export const PARTICLES = {
  density: 1,
  sizeTimes: 1,
  drift: 1,
  current: 1,
  turbulence: 0.25,
  layers: [
    { radiusM: 4, perM3: 7, sizeM: [0.005, 0.01] },
    { radiusM: 10, perM3: 1.2, sizeM: [0.009, 0.018] },
    { radiusM: 22, perM3: 0.16, sizeM: [0.014, 0.03] },
  ],
  planktonRef: { zoo: 0.4, chl: 0.1 },
  planktonSpan: [0.4, 2.5],
  most: 70_000,
  opacity: 1.4,
  nightLight: 0.12,
  forward: 0.9,
  inShaft: 1.6,
  maxPx: 3,
  glint: { share: 0.05, everyS: [4, 11], sharp: 48, intensity: 1.1, depthPower: 2, warmBelowDeg: 35 },
  fade: { fromShare: 0.7, nearM: 0.35 },
  byDepth: [
    [0, 1],
    [6, 1],
    [18, 0.85],
    [30, 0.7],
  ],
  zoomOut: { share: 0.35, nearM: 8, farM: 30 },
  quality: { computer: 1, phone: 0.55 },
  adapt: { slowS: 1 / 25, quickS: 1 / 28, perS: 0.25, fewest: 0.3 },
  easeS: 1.5,
  palette: [0xdcecf6, 0xefe6cf, 0x9fd3cb, 0xe3cf98, 0xb9ae96],
  paletteShare: [0.34, 0.26, 0.2, 0.1, 0.1],
} as const;

/**
 * The sediment a parrotfish's bite sends up (the brief's 4, "Bottom-dwelling animals disturbing sediment", "Use existing
 * animal behaviors"): the parrotfish scrape the reef's rock as they bite it (Ong and Holland 2010, below), at the foot of
 * each of their schools' feeding dives (`feeding`, `FishSchools`); a cloud of `grains`, `fine` of them fine, spreading
 * out to `spreadM` and up to `riseM` over `spreadS`, carried by the current, each settling at its grain's speed and
 * fading over `lifeS`; a bite making one `perBite` of the time, none further than `withinM` from the camera; at most
 * `clouds` at once (the oldest reused). `intensity` their opacity's times (not their number's). `colors` pale beige-gray.
 *
 * The grains' settling (`settleMPerS`) by Ferguson and Church's 2004 law for natural grains, w = R g D² / (C1 ν +
 * (0.75 C2 R g D³)^0.5), its constants for grains of middling shape C1 18 and C2 1.0 (as Murch et al. 2020, *Front.
 * Earth Sci.* 8: 237, p. 10, give them, CC BY; the original unread, its host refused here), R the grain's density over
 * seawater's less one, the reef's carbonate grains 2.51 to 2.76 g/cm³ (Ningaloo's, Cuttler et al. 2017 as Rosenberger
 * et al. 2020, *Front. Mar. Sci.* 7: 513020, p. 5, give them, CC BY), seawater 1.025 g/cm³ and ν 0.94 mm²/s at 25 °C:
 * a fine grain of 0.0625 mm (silt's coarsest) 3.0 to 3.5 mm/s, a coarse one of 0.3 mm (medium sand) 38 to 42 mm/s,
 * COMPUTED. That the parrotfish's bites take the reef's carbonate away: van Woesik, Li and Asner 2025 (*PLOS ONE*,
 * e0324197, p. 7, CC BY; a bite's volume by the fish's length, from Ong and Holland 2010's Hawaiian parrotfish, whose
 * paper's host is refused here). How much of a bite is seen as a cloud, and how often (`perBite`), JUDGED: no source
 * read measures the puffs.
 */
export const SEDIMENT = {
  grazers: ['bullethead', 'palenose', 'redlip'],
  intensity: 1,
  perBite: 0.35,
  withinM: 22,
  clouds: 14,
  grains: 36,
  fine: 0.6,
  spreadM: [0.25, 0.6],
  riseM: [0.15, 0.4],
  spreadS: 1.6,
  lifeS: { fine: 9, coarse: 4.5 },
  settleMPerS: { fine: 0.003, coarse: 0.04 },
  opacity: 1,
  sizeM: [0.012, 0.026],
  colors: [0xd9cfb6, 0xbcb3a0, 0xe8e0c8],
} as const;

/**
 * Bubbles (the brief's 5: "rare secondary effects", "Wave turbulence near the water surface"): small groups of `count`
 * just under the surface, `depthM` down, the waves' breaking having carried them under (the trade winds blow over Maui
 * Nui most days, "between 80-90% of the year", Longman et al. 2015, PCSU Tech. Rep. 193, p. 12, CC0; their median at
 * Kahului 5.9 m/s, Garza 2011's thesis, p. 15, MEASURED 1984 to 2009; a breaking wave's bubbles carried down -0.83 +
 * 0.481 U10 m, Vagle et al. 2010's fit as Cifuentes-Lorenzen et al. 2023, *JGR Oceans* 128, p. 9, give it: 2.0 m at
 * 5.9 m/s, COMPUTED; the groups' rate JUDGED, the simulation holding no wind or waves); one group every `everyS`
 * seconds on the mean where the camera sees the water there (none the camera could not see), each bubble rising at its
 * size's speed (`riseMPerS` by its diameter, mm; JUDGED, the measured speeds read being of bubbles 2 to 10 mm across,
 * 15 to 37 cm/s, Veloso-Alarcón et al. 2022, *Front. Earth Sci.* 10: 858992, p. 1; Leifer et al. 2000's in seawater
 * unread, their host refused here), the larger wobbling (`wobbleFromMm`, JUDGED), gone at the surface; drawn
 * `drawnTimes` its size, at most `maxPx` pixels across (more than the suspended's, so a ring reads), a ring of pale
 * blue-white with a bright pixel at its top, `opacity` its own. `frequency` their rate's times.
 */
export const BUBBLES = {
  frequency: 1,
  everyS: 40,
  count: [4, 9],
  depthM: [0.3, 2],
  withinM: [5, 18],
  groups: 6,
  diameterMm: [0.4, 2.6],
  riseMPerS: [
    [0.4, 0.05],
    [1, 0.12],
    [2, 0.22],
    [3, 0.24],
  ],
  wobbleFromMm: 1.6,
  wobbleM: 0.03,
  drawnTimes: 9,
  maxPx: 5,
  opacity: 0.9,
  spreadM: 0.25,
  colors: [0xd8f2ff, 0xbfe4f5],
} as const;

/** The longest a group of bubbles takes to reach the surface, s: from its deepest at its slowest, and a second. */
const BUBBLE_LIFE_S = (BUBBLES.depthM[1] + BUBBLES.spreadM) / (BUBBLES.riseMPerS[0] as readonly [number, number])[1] + 1;

/** The effects each of which can be turned off, for benchmarking (the brief's 10: "a simple way to disable individual effects"). */
const EFFECTS = ['motes', 'glints', 'sediment', 'bubbles', 'shafts'] as const;
export type Effect = (typeof EFFECTS)[number];

/** What the particles are drawn with: the effects on, the device's quality's share and any density asked. */
export interface ParticleSettings {
  on: Record<Effect, boolean>;
  quality: keyof typeof PARTICLES.quality;
  density: number;
}

/**
 * The settings from the page's address and the device (the brief's 10: "Implement appropriate desktop and mobile quality
 * settings", "a simple way to disable individual effects"): `?fx=off` all off; `?fx=no-glints,no-bubbles` those off;
 * `phone` or `computer` that quality; `density=0.5` that many. A phone's quality where the pointer is coarse.
 */
export function particleSettings(search: string, coarse: boolean): ParticleSettings {
  const on = Object.fromEntries(EFFECTS.map((e) => [e, true])) as Record<Effect, boolean>;
  let quality: ParticleSettings['quality'] = coarse ? 'phone' : 'computer';
  let density = 1;
  const fx = new URLSearchParams(search).get('fx');
  for (const word of (fx ?? '').split(',').map((w) => w.trim()).filter(Boolean)) {
    if (word === 'off') for (const e of EFFECTS) on[e] = false;
    else if (word.startsWith('no-') && (EFFECTS as readonly string[]).includes(word.slice(3))) on[word.slice(3) as Effect] = false;
    else if (word === 'phone' || word === 'computer') quality = word;
    else if (word.startsWith('density=')) {
      const d = Number(word.slice(8));
      if (Number.isFinite(d) && d >= 0) density = d;
    }
  }
  return { on, quality, density };
}

/** The page's own settings: its address's and its pointer's; a computer's, everything on, where there is no page. */
export function pageParticleSettings(): ParticleSettings {
  const search = typeof location === 'undefined' ? '' : location.search;
  const coarse = typeof matchMedia === 'undefined' ? false : matchMedia('(pointer: coarse)').matches;
  return particleSettings(search, coarse);
}

/** The glints' warm white at noon and their gold with the sun low. */
const WARM_NOON = new THREE.Color(0xfff4dc);
const WARM_LOW = new THREE.Color(0xf2c878);

/** The clock's period, s: every eddy and glint repeats a whole number of times in it, so its wrap moves none. */
const PERIOD_S = 3600;
/** The depths the light is laid at for the shaders, a meter apart from the surface. */
const DEPTHS = 33;
/** The most shafts the particles are lit by. */
const MOST_SHAFTS = 8;

/** A beam the particles are lit in: its top, half its width and its brightness now (`LightShafts.beams`). */
export interface Beam {
  top: THREE.Vector3;
  halfWidthM: number;
  bright: number;
}

const LIGHT = /* glsl */ `
uniform float uLight[${DEPTHS}];
uniform float uVis[${DEPTHS}];
uniform float uEyeLight;
uniform float uPxPerM;
uniform float uPixelRatio;
uniform float uMaxPx;
// The share of the surface's light at a depth, m (lightShare's, laid a meter apart); that against the share at the
// camera's depth the eye is balanced to (as the floor and the animals are seen, PaintedFloor.adapt, HabitatWater.seen);
// and the particles' visibility there.
float shareAt(float depth) {
  float d = clamp(depth, 0.0, ${(DEPTHS - 1).toFixed(1)} - 1e-3);
  int i = int(floor(d));
  return mix(uLight[i], uLight[i + 1], fract(d));
}
float lightAt(float depth) {
  return shareAt(depth) / uEyeLight;
}
float visAt(float depth) {
  float d = clamp(depth, 0.0, ${(DEPTHS - 1).toFixed(1)} - 1e-3);
  int i = int(floor(d));
  return mix(uVis[i], uVis[i + 1], fract(d));
}
// A particle sizeM across, as many whole pixels across as it covers (at least one, at most uMaxPx), and the share of
// them it covers: its opacity, so it grows and dims without a jump as it nears or recedes.
vec2 pixelsOf(float sizeM, float away) {
  float px = sizeM * uPxPerM / max(away, 0.05);
  float drawn = clamp(ceil(px), 1.0, uMaxPx);
  return vec2(drawn, min(1.0, px * px / (drawn * drawn)) * min(1.0, uMaxPx / px));
}
`;

const SUSPENDED_VERTEX = /* glsl */ `
attribute float aLayer;
attribute float aSize;
attribute vec3 aColor;
attribute vec4 aWave;
attribute vec4 aPhase;
attribute float aRank;
uniform float uRadius[3];
uniform float uTurn;
uniform float uShown;
uniform vec3 uOffset[3];
uniform float uClock;
uniform float uTurb;
uniform float uSurface;
uniform float uSun;
uniform float uNight;
uniform vec3 uSlant;
uniform float uForward;
uniform float uInShaft;
uniform vec4 uShaft[${MOST_SHAFTS}];
uniform float uShaftBright[${MOST_SHAFTS}];
uniform float uShaftDown;
uniform float uGlintShare;
uniform float uGlintSharp;
uniform float uGlintGain;
uniform float uGlintPower;
uniform vec3 uWarm;
uniform float uOpacity;
uniform float uZoom;
uniform float uFadeFrom;
uniform float uNearM;
${WATER_FOG}
${LIGHT}
varying vec4 vColor;
varying float vDrawn;
const float TAU = 6.2831853;
void main() {
  int layer = int(aLayer + 0.5);
  float radius = uRadius[layer];
  float side = 2.0 * radius;
  // Its place in its shell's box, carried by the current, turned on its own slow eddy, a whole number of turns (aWave) in
  // the clock's period, uTurn the share of it gone, so the clock's wrap moves it not at all; its vertical swing the
  // smaller.
  vec3 p = position * side + uOffset[layer];
  vec3 eddy = vec3(sin(TAU * fract(aWave.x * uTurn + aPhase.x)), 0.6 * sin(TAU * fract(aWave.y * uTurn + aPhase.y)), sin(TAU * fract(aWave.z * uTurn + aPhase.z)));
  p += uTurb * (0.6 + 0.4 * aPhase.w) * eddy;
  // The copy of it a box apart that lies in the box about the camera: the water near it full of them wherever it goes.
  vec3 rel = mod(p - cameraPosition + radius, side) - radius;
  vec3 world = cameraPosition + rel;
  float away = length(rel);
  vec4 mv = viewMatrix * vec4(world, 1.0);
  gl_Position = projectionMatrix * mv;
  vec2 px = pixelsOf(aSize, -mv.z);
  vDrawn = px.x;
  gl_PointSize = px.x * uPixelRatio;
  // Faded out toward its shell's edge, where it wraps, and close to the camera; none over the surface.
  float fade = (1.0 - smoothstep(uFadeFrom * radius, radius, away)) * smoothstep(uNearM, 2.0 * uNearM, away) * step(world.y, uSurface - 0.03);
  float depth = -world.y;
  float share = shareAt(depth);
  float light = share / uEyeLight;
  // In a shaft's light: within its width, from its top down, as bright as its picture there.
  float shaft = 0.0;
  for (int k = 0; k < ${MOST_SHAFTS}; k++) {
    vec3 v = world - uShaft[k].xyz;
    float along = dot(v, uSlant);
    float across = length(v - along * uSlant);
    float inside = 1.0 - smoothstep(0.35 * uShaft[k].w, uShaft[k].w, across);
    float down = clamp(1.0 - along / uShaftDown, 0.0, 1.0) * step(0.0, along);
    shaft += uShaftBright[k] * inside * pow(down, 1.5);
  }
  // Brighter looking toward the sun, as fine particles scatter its light forward.
  float toward = pow(max(0.0, dot(rel / max(away, 1e-4), -uSlant)), 4.0);
  float lit = uNight + uSun * light * (1.0 + uForward * toward + uInShaft * shaft);
  // A few catch the sun for a moment as they turn, the more in a shaft, little deep down: by the light's own share at
  // their depth, not the eye's balance (M173's review: by that, a glint 25 m down beside the camera shone as in the
  // shallows).
  float glint = 0.0;
  if (aPhase.w < uGlintShare) {
    float turn = fract(aWave.w * uTurn + aPhase.x + aPhase.z);
    glint = pow(max(0.0, sin(TAU * turn)), uGlintSharp) * uGlintGain * uSun * pow(share, uGlintPower) * (1.0 + 2.0 * shaft);
  }
  // Bluer with depth, as the water's light is.
  vec3 color = mix(aColor, uFogColor, 0.5 * (1.0 - share)) * min(1.4, lit) + uWarm * glint;
  // The last 3% of those shown faded, so as their number moves (uShown, eased) each fades in or out and none pops.
  float shown = clamp((uShown - aRank) / max(1.0, 0.03 * uShown), 0.0, 1.0);
  float keep = (1.0 - waterFog(world).a) * fade * uZoom * visAt(depth) * px.y * shown;
  vColor = vec4(color, clamp((uOpacity * min(1.0, lit) + glint) * keep, 0.0, 1.0));
}
`;

const SUSPENDED_FRAGMENT = /* glsl */ `
varying vec4 vColor;
varying float vDrawn;
void main() {
  // A crisp square of whole pixels; the largest, three across, with its corners soft.
  vec2 cell = floor(gl_PointCoord * vDrawn);
  float corner = vDrawn > 2.5 && (cell.x == 0.0 || cell.x == vDrawn - 1.0) && (cell.y == 0.0 || cell.y == vDrawn - 1.0) ? 0.35 : 1.0;
  gl_FragColor = vec4(vColor.rgb, vColor.a * corner);
  #include <colorspace_fragment>
}
`;

const SEDIMENT_VERTEX = /* glsl */ `
attribute vec4 aCloud;
attribute vec4 aGrain;
attribute vec3 aColor;
uniform float uClock;
uniform vec3 uCurrent;
uniform float uSpreadS;
uniform vec2 uLife;
uniform vec2 uSettle;
uniform float uOpacity;
uniform float uSize;
uniform float uSun;
uniform float uNight;
uniform float uSurface;
${WATER_FOG}
${LIGHT}
varying vec4 vColor;
varying float vDrawn;
void main() {
  // Its cloud's floor point and when it was sent up (aCloud.w, the clock's seconds), its own way out, up and how far
  // (aGrain.xyz, m), coarse or fine (aGrain.w).
  float age = mod(uClock - aCloud.w + ${PERIOD_S.toFixed(1)}, ${PERIOD_S.toFixed(1)});
  float coarse = aGrain.w;
  float life = mix(uLife.x, uLife.y, coarse);
  float out_ = 1.0 - exp(-age / uSpreadS);
  vec3 p = aCloud.xyz + vec3(aGrain.x, 0.0, aGrain.z) * out_ + uCurrent * age * mix(1.0, 0.4, coarse);
  // Up as it is thrown, down at its grain's speed, never under the floor it rose from.
  p.y = max(aCloud.y, aCloud.y + aGrain.y * out_ - mix(uSettle.x, uSettle.y, coarse) * age);
  p.y = min(p.y, uSurface - 0.05);
  vec4 mv = viewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mv;
  vec2 px = pixelsOf(uSize * (0.6 + 0.8 * fract(aGrain.x * 37.0 + aGrain.z * 91.0)), -mv.z);
  vDrawn = px.x;
  gl_PointSize = px.x * uPixelRatio;
  // Fading as it spreads, gone at its life's end; none before it is sent.
  float alive = step(age, life) * step(0.0, aCloud.w);
  float fade = alive * (1.0 - smoothstep(0.15 * life, life, age)) * smoothstep(0.0, 0.15, age);
  float lit = uNight + uSun * lightAt(-p.y);
  vColor = vec4(aColor * min(1.2, lit), clamp(uOpacity * fade * min(1.0, lit) * (1.0 - waterFog(p).a) * px.y, 0.0, 1.0));
}
`;

const BUBBLE_VERTEX = /* glsl */ `
attribute vec4 aStart;
attribute vec4 aBubble;
attribute vec3 aColor;
uniform float uClock;
uniform float uSurface;
uniform float uSun;
uniform float uNight;
uniform float uWobbleM;
uniform float uWobbleFrom;
uniform float uDrawnTimes;
uniform vec3 uCurrent;
uniform float uOpacity;
${WATER_FOG}
${LIGHT}
varying vec4 vColor;
varying float vDrawn;
void main() {
  // Its start and when (aStart), its diameter, mm, its rise, m/s, its wobble's phase and pace (aBubble).
  float age = mod(uClock - aStart.w + ${PERIOD_S.toFixed(1)}, ${PERIOD_S.toFixed(1)});
  float wobble = smoothstep(uWobbleFrom - 0.3, uWobbleFrom + 0.3, aBubble.x) * uWobbleM;
  float turn = 6.2831853 * (aBubble.z + age * aBubble.w);
  vec3 p = aStart.xyz + vec3(wobble * sin(turn), aBubble.y * age, wobble * cos(turn)) + uCurrent * age;
  vec4 mv = viewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mv;
  vec2 px = pixelsOf(uDrawnTimes * aBubble.x * 0.001, -mv.z);
  vDrawn = px.x;
  gl_PointSize = px.x * uPixelRatio;
  // Gone as it reaches the surface; none before it is sent.
  float alive = step(p.y, uSurface - 0.02) * step(0.0, aStart.w) * smoothstep(0.0, 0.3, age);
  float lit = max(0.35, uNight + uSun * lightAt(-p.y));
  vColor = vec4(aColor * min(1.3, lit), clamp(uOpacity * alive * min(1.0, lit) * (1.0 - waterFog(p).a) * px.y, 0.0, 1.0));
}
`;

const BUBBLE_FRAGMENT = /* glsl */ `
varying vec4 vColor;
varying float vDrawn;
void main() {
  // A ring of whole pixels, its middle clearer, a bright pixel at its upper left; one or two pixels a pale square.
  vec2 cell = floor(gl_PointCoord * vDrawn);
  float a = 1.0;
  vec3 rgb = vColor.rgb;
  if (vDrawn > 2.5) {
    vec2 mid = vec2((vDrawn - 1.0) / 2.0);
    float r = length(cell - mid);
    if (r > mid.x + 0.6) discard;
    a = r > mid.x - 0.9 ? 1.0 : 0.3;
    if (cell.x == floor(mid.x - 0.5) && cell.y == floor(mid.y - 0.5)) rgb = vec3(1.0);
  }
  gl_FragColor = vec4(rgb, vColor.a * a);
  #include <colorspace_fragment>
}
`;

/** A fog's uniforms, to be given the water's (`HabitatWater.fog`). */
/** The value of a piecewise-straight table of [x, y] at x, flat past its ends. */
export function along(table: readonly (readonly [number, number])[], x: number): number {
  const first = table[0] as readonly [number, number];
  if (x <= first[0]) return first[1];
  for (let i = 1; i < table.length; i++) {
    const [x1, y1] = table[i] as readonly [number, number];
    const [x0, y0] = table[i - 1] as readonly [number, number];
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  }
  return (table[table.length - 1] as readonly [number, number])[1];
}

/** Each shell's particles at the plankton's reference: its box's, 2 radii on a side. */
const LAYER_COUNTS = PARTICLES.layers.map((l) => l.perM3 * (2 * l.radiusM) ** 3);
/** Each shell's share of them all. */
const LAYER_SHARES = LAYER_COUNTS.map((n) => n / LAYER_COUNTS.reduce((a, b) => a + b, 0));

/** How many of the suspended particles are drawn for the day's plankton, the settings and the device's share now. */
export function suspendedCount(zooMmolNPerM3: number, chlorophyllMgM3: number, share: number): number {
  const p = PARTICLES;
  const plankton = Math.min(p.planktonSpan[1], Math.max(p.planktonSpan[0], 0.5 * (zooMmolNPerM3 / p.planktonRef.zoo) + 0.5 * (chlorophyllMgM3 / p.planktonRef.chl)));
  const all = LAYER_COUNTS.reduce((a, b) => a + b, 0) * plankton;
  return Math.min(p.most, Math.round(all * p.density * share));
}

/** A cloud's or a bubbles' group's `each` particles, the `slot`th in `points`' attribute `name`, hidden for good (its w -1). */
function hideSlot(points: THREE.Points, name: string, slot: number, each: number): void {
  const a = points.geometry.getAttribute(name) as THREE.BufferAttribute;
  for (let k = 0; k < each; k++) a.setW(slot * each + k, -1);
  a.addUpdateRange(slot * each * 4, each * 4);
  a.needsUpdate = true;
}

/** A frame's state the particles are drawn by: the camera, the shafts' beams and how the frame went. */
export interface ParticleFrame {
  camera: THREE.Camera;
  seconds: number;
  calm: boolean;
  beams: readonly Beam[];
  /** How far the camera looks to the reef, m: its line of sight's meeting with the floor, or its far reach. */
  viewM: number;
}

export class ReefParticles {
  readonly group = new THREE.Group();
  readonly suspended: THREE.Points;
  readonly sediment: THREE.Points;
  readonly bubbles: THREE.Points;
  /** The fogs of all three, for the water to lay its own on. */
  readonly fogs: FogUniforms[] = [];
  private readonly uniforms: Record<string, THREE.IUniform>[] = [];
  private readonly offsets = PARTICLES.layers.map(() => new THREE.Vector3());
  private readonly current = new THREE.Vector3();
  private clock = 0;
  private plankton: { zoo: number; chl: number } = { zoo: PARTICLES.planktonRef.zoo, chl: PARTICLES.planktonRef.chl };
  /** The device's share of the particles now: its quality's, lowered while its frames run slow (`adapt`). */
  private share: number;
  private slowFor = 0;
  private quickFor = 0;
  private nextCloud = 0;
  private nextGroup = 0;
  private sinceGroup = 0;
  private emitted = 0;
  private sentGroups = 0;
  private readonly buffer = new THREE.Vector2();
  private kdPar = 0.07;
  /** How many suspended particles are shown now, eased toward the count asked (`recount`), and whether the day's water has yet been laid (the first laid at once). */
  private shown = 0;
  private laid = false;
  /** The camera's draw-back's fade now, eased toward the view's (`zoomOut`). */
  private zoom = 1;
  /** When each cloud and each bubbles' group was sent, the clock's seconds; -1 for none, or gone. */
  private readonly cloudBorn: number[] = new Array<number>(SEDIMENT.clouds).fill(-1);
  private readonly groupBorn: number[] = new Array<number>(BUBBLES.groups).fill(-1);
  /** Tenths of a second the bubbles' chance has been drawn for. */
  private ticks = 0;
  private readonly at = new THREE.Vector3();

  /**
   * @param seed the reef's seed, for where each particle stands
   * @param settings the effects on and the device's quality (`pageParticleSettings`)
   * @param currentMPerS the reef's current, m/s, scene axes (along the reef, z, for Maui Nui's: `REEF_CURRENT`)
   */
  constructor(private readonly seed: number, readonly settings: ParticleSettings, currentMPerS: readonly [number, number, number]) {
    this.share = PARTICLES.quality[settings.quality] * settings.density;
    this.current.set(...currentMPerS).multiplyScalar(PARTICLES.current);
    this.suspended = this.makeSuspended();
    this.sediment = this.makeSediment();
    this.bubbles = this.makeBubbles();
    for (const points of [this.suspended, this.sediment, this.bubbles]) {
      points.frustumCulled = false;
      // After the rock, the coral and the shafts (3.2), before the octopuses and the fish, which write no depth, so it is
      // never laid over one in front of it.
      points.renderOrder = 3.3;
      points.raycast = () => undefined;
      // The canvas's pixels a meter off, read as it is drawn.
      points.onBeforeRender = (renderer, _scene, camera) => this.prepare(renderer, camera as THREE.PerspectiveCamera);
      this.group.add(points);
    }
    this.suspended.visible = settings.on.motes;
    this.sediment.visible = settings.on.sediment;
    this.bubbles.visible = settings.on.bubbles;
    this.layLight(0.07);
  }

  private material(vertexShader: string, fragmentShader: string, own: Record<string, THREE.IUniform>): THREE.ShaderMaterial {
    const fog = noFog();
    this.fogs.push(fog);
    const uniforms: Record<string, THREE.IUniform> = {
      ...fog,
      ...own,
      uClock: { value: 0 },
      uSurface: { value: 0 },
      uSun: { value: 1 },
      uNight: { value: PARTICLES.nightLight },
      uLight: { value: new Array<number>(DEPTHS).fill(1) },
      uVis: { value: new Array<number>(DEPTHS).fill(1) },
      uEyeLight: { value: 1 },
      uPxPerM: { value: 800 },
      uPixelRatio: { value: 1 },
      uMaxPx: { value: PARTICLES.maxPx },
    };
    this.uniforms.push(uniforms);
    return new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, transparent: true, depthWrite: false, depthTest: true });
  }

  private makeSuspended(): THREE.Points {
    const p = PARTICLES;
    const n = p.most;
    const position = new Float32Array(n * 3);
    const layer = new Float32Array(n);
    const size = new Float32Array(n);
    const color = new Float32Array(n * 3);
    const wave = new Float32Array(n * 4);
    const phase = new Float32Array(n * 4);
    const palette = p.palette.map((hex) => new THREE.Color(hex));
    const h = (i: number, k: number): number => hash3(this.seed, i, 1_730 + k);
    for (let i = 0; i < n; i++) {
      position.set([h(i, 0), h(i, 1), h(i, 2)], i * 3);
      // Its shell by their shares, so the first so many of them, however many are drawn, hold each shell's share.
      let pick = h(i, 3);
      let l = 0;
      while (l < p.layers.length - 1 && pick >= (LAYER_SHARES[l] as number)) pick -= LAYER_SHARES[l++] as number;
      const shell = p.layers[l] as (typeof p.layers)[number];
      layer[i] = l;
      size[i] = (shell.sizeM[0] + (shell.sizeM[1] - shell.sizeM[0]) * h(i, 4) ** 2) * p.sizeTimes;
      let c = h(i, 5);
      let k = 0;
      while (k < p.palette.length - 1 && c >= (p.paletteShare[k] as number)) c -= p.paletteShare[k++] as number;
      const col = palette[k] as THREE.Color;
      color.set([col.r, col.g, col.b], i * 3);
      // Its eddies' turns in the clock's period, whole: 0.05 to 0.15 a second, at `drift`'s pace; its glint's every 4 to 11 s.
      const turns = (lo: number, hi: number, r: number): number => Math.max(1, Math.round(PERIOD_S * p.drift * (lo + (hi - lo) * r)));
      const every = p.glint.everyS[0] + (p.glint.everyS[1] - p.glint.everyS[0]) * h(i, 9);
      wave.set([turns(0.05, 0.15, h(i, 6)), turns(0.04, 0.1, h(i, 7)), turns(0.05, 0.15, h(i, 8)), Math.max(1, Math.round(PERIOD_S / every))], i * 4);
      phase.set([h(i, 10), h(i, 11), h(i, 12), h(i, 13)], i * 4);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
    geometry.setAttribute('aLayer', new THREE.BufferAttribute(layer, 1));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(color, 3));
    geometry.setAttribute('aWave', new THREE.BufferAttribute(wave, 4));
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phase, 4));
    geometry.setAttribute('aRank', new THREE.BufferAttribute(Float32Array.from({ length: n }, (_, i) => i), 1));
    this.shown = suspendedCount(this.plankton.zoo, this.plankton.chl, this.share);
    geometry.setDrawRange(0, Math.ceil(this.shown));
    const material = this.material(SUSPENDED_VERTEX, SUSPENDED_FRAGMENT, {
      uRadius: { value: p.layers.map((l) => l.radiusM) },
      uTurn: { value: 0 },
      uShown: { value: this.shown },
      uOffset: { value: this.offsets },
      uTurb: { value: p.turbulence },
      uSlant: { value: new THREE.Vector3(0, -1, 0) },
      uForward: { value: p.forward },
      uInShaft: { value: p.inShaft },
      uShaft: { value: Array.from({ length: MOST_SHAFTS }, () => new THREE.Vector4(0, 1e6, 0, 1)) },
      uShaftBright: { value: new Array<number>(MOST_SHAFTS).fill(0) },
      uShaftDown: { value: SHAFTS.downM },
      uGlintShare: { value: this.settings.on.glints ? p.glint.share : 0 },
      uGlintSharp: { value: p.glint.sharp },
      uGlintGain: { value: p.glint.intensity },
      uGlintPower: { value: p.glint.depthPower },
      uWarm: { value: WARM_NOON.clone() },
      uOpacity: { value: p.opacity },
      uZoom: { value: 1 },
      uFadeFrom: { value: p.fade.fromShare },
      uNearM: { value: p.fade.nearM },
    });
    return new THREE.Points(geometry, material);
  }

  private makeSediment(): THREE.Points {
    const s = SEDIMENT;
    const n = s.clouds * s.grains;
    const geometry = new THREE.BufferGeometry();
    // Its points' places are the shader's; three wants one to count them by.
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    // None sent yet: a start before the clock's (w < 0) hides it.
    geometry.setAttribute('aCloud', new THREE.BufferAttribute(new Float32Array(n * 4).fill(-1), 4).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('aGrain', new THREE.BufferAttribute(new Float32Array(n * 4), 4).setUsage(THREE.DynamicDrawUsage));
    const color = new Float32Array(n * 3);
    const colors = s.colors.map((hex) => new THREE.Color(hex));
    for (let i = 0; i < n; i++) {
      const c = colors[Math.floor(hash3(this.seed, i, 1_760) * colors.length)] as THREE.Color;
      color.set([c.r, c.g, c.b], i * 3);
    }
    geometry.setAttribute('aColor', new THREE.BufferAttribute(color, 3));
    const material = this.material(SEDIMENT_VERTEX, SUSPENDED_FRAGMENT, {
      uCurrent: { value: this.current },
      uSpreadS: { value: s.spreadS },
      uLife: { value: new THREE.Vector2(s.lifeS.fine, s.lifeS.coarse) },
      uSettle: { value: new THREE.Vector2(s.settleMPerS.fine, s.settleMPerS.coarse) },
      uOpacity: { value: s.opacity * s.intensity },
      uSize: { value: ((s.sizeM[0] + s.sizeM[1]) / 2) * PARTICLES.sizeTimes },
    });
    return new THREE.Points(geometry, material);
  }

  private makeBubbles(): THREE.Points {
    const b = BUBBLES;
    const n = b.groups * b.count[1];
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geometry.setAttribute('aStart', new THREE.BufferAttribute(new Float32Array(n * 4).fill(-1), 4).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('aBubble', new THREE.BufferAttribute(new Float32Array(n * 4), 4).setUsage(THREE.DynamicDrawUsage));
    const color = new Float32Array(n * 3);
    const colors = b.colors.map((hex) => new THREE.Color(hex));
    for (let i = 0; i < n; i++) {
      const c = colors[i % colors.length] as THREE.Color;
      color.set([c.r, c.g, c.b], i * 3);
    }
    geometry.setAttribute('aColor', new THREE.BufferAttribute(color, 3));
    const material = this.material(BUBBLE_VERTEX, BUBBLE_FRAGMENT, {
      uWobbleM: { value: b.wobbleM },
      uWobbleFrom: { value: b.wobbleFromMm },
      uDrawnTimes: { value: b.drawnTimes * PARTICLES.sizeTimes },
      uCurrent: { value: this.current },
      uOpacity: { value: b.opacity },
    });
    (material.uniforms.uMaxPx as THREE.IUniform).value = b.maxPx;
    return new THREE.Points(geometry, material);
  }

  /** The light's share at each meter of depth on the day's clarity, and the particles' visibility there, for the shaders. */
  private layLight(kdPar: number): void {
    this.kdPar = kdPar;
    for (const u of this.uniforms) {
      const light = u.uLight?.value as number[];
      const vis = u.uVis?.value as number[];
      for (let d = 0; d < DEPTHS; d++) {
        light[d] = lightShare(kdPar, d);
        vis[d] = along(PARTICLES.byDepth, d);
      }
    }
  }

  /** The day's water (the simulation's, `HabitatEnvironment`): the light by its clarity, their number by its plankton. */
  setState(kdPar: number, chlorophyllMgM3: number, zooMmolNPerM3: number): void {
    this.layLight(kdPar);
    this.plankton = { zoo: Math.max(0, zooMmolNPerM3), chl: Math.max(0, chlorophyllMgM3) };
    if (!this.laid) this.shown = this.wanted();
    this.laid = true;
    this.recount();
  }

  /** How many suspended particles the day's plankton and the device's share now ask for. */
  private wanted(): number {
    return suspendedCount(this.plankton.zoo, this.plankton.chl, this.share);
  }

  /** The draw range: all those shown and those fading in toward the count asked. */
  private recount(): void {
    this.suspended.geometry.setDrawRange(0, Math.ceil(Math.max(this.shown, this.wanted())));
    ((this.uniforms[0] as Record<string, THREE.IUniform>).uShown as THREE.IUniform).value = this.shown;
  }

  /**
   * The light at an hour: the sun's on a level surface against noon's (`HourSun.level`), the moon's a thousandth as
   * much as the water's (`HabitatWater.setLight`); its beam's slant in the water (the shafts', `LightShafts.slant`); the
   * glints' warmth as the sun is low (the brief's 7: "Morning: Softer angled sunlight and subtle warm highlights").
   */
  setLight(sun: HourSun, slant: THREE.Vector3, surfaceY: number): void {
    const level = Math.min(1, Math.max(0, sun.level));
    const night = PARTICLES.nightLight * (0.4 + 0.6 * (sun.moon?.level ?? 0));
    const warm = 1 - Math.min(1, Math.max(0, sun.elevationDeg / PARTICLES.glint.warmBelowDeg));
    for (const u of this.uniforms) {
      (u.uSun as THREE.IUniform).value = level;
      (u.uNight as THREE.IUniform).value = night;
      (u.uSurface as THREE.IUniform).value = surfaceY;
      if (u.uSlant) (u.uSlant.value as THREE.Vector3).copy(slant);
      if (u.uWarm) (u.uWarm.value as THREE.Color).copy(WARM_NOON).lerp(WARM_LOW, warm);
    }
  }

  /**
   * A frame: the clock and the current moved on by the player's `seconds` (none when `calm`, the player asking for less
   * motion: still, as the fish keep within themselves), the shafts' beams laid, the camera's draw-back faded, the
   * device's share kept to its frames (`adapt`), the bubbles' groups sent.
   */
  update(frame: ParticleFrame): void {
    const run = frame.calm ? 0 : Math.min(frame.seconds, 0.25);
    this.clock = (this.clock + run) % PERIOD_S;
    for (let k = 0; k < PARTICLES.layers.length; k++) {
      const side = 2 * (PARTICLES.layers[k] as (typeof PARTICLES.layers)[number]).radiusM;
      const o = this.offsets[k] as THREE.Vector3;
      o.addScaledVector(this.current, run);
      o.set(((o.x % side) + side) % side, ((o.y % side) + side) % side, ((o.z % side) + side) % side);
    }
    // The draw-back's fade and the number shown eased over `easeS` of the player's seconds, calm or not, so neither
    // jumps (M173's review: the line of sight slipping off a ridge onto the floor behind dimmed them all at once).
    const ease = Math.min(1, Math.max(0, frame.seconds) / PARTICLES.easeS);
    const z = PARTICLES.zoomOut;
    this.zoom += (1 - z.share * Math.min(1, Math.max(0, (frame.viewM - z.nearM) / (z.farM - z.nearM))) - this.zoom) * ease;
    const wanted = this.wanted();
    if (this.shown !== wanted) {
      this.shown = Math.abs(wanted - this.shown) < 1 ? wanted : this.shown + (wanted - this.shown) * ease;
      this.recount();
    }
    const eye = lightShare(this.kdPar, Math.max(0, -frame.camera.position.y));
    for (const u of this.uniforms) {
      (u.uClock as THREE.IUniform).value = this.clock;
      (u.uEyeLight as THREE.IUniform).value = eye;
      if (u.uZoom) u.uZoom.value = this.zoom;
      if (u.uTurn) u.uTurn.value = this.clock / PERIOD_S;
    }
    this.expire();
    const su = this.uniforms[0] as Record<string, THREE.IUniform>;
    const tops = su.uShaft?.value as THREE.Vector4[];
    const bright = su.uShaftBright?.value as number[];
    for (let k = 0; k < MOST_SHAFTS; k++) {
      const beam = frame.beams[k];
      if (beam && this.settings.on.shafts) {
        (tops[k] as THREE.Vector4).set(beam.top.x, beam.top.y, beam.top.z, beam.halfWidthM);
        bright[k] = beam.bright;
      } else bright[k] = 0;
    }
    this.adapt(frame.seconds);
    if (this.settings.on.bubbles && !frame.calm) this.sendBubbles(frame.camera, run);
  }

  /**
   * The clouds and the bubbles' groups gone from view hidden for good once past their lives (M173's review: the clock's
   * wrap an hour on would have brought one not sent again back where it was).
   */
  private expire(): void {
    const life = Math.max(SEDIMENT.lifeS.fine, SEDIMENT.lifeS.coarse) + 1;
    for (let slot = 0; slot < this.cloudBorn.length; slot++) {
      const born = this.cloudBorn[slot] as number;
      if (born < 0 || this.age(born) <= life) continue;
      this.cloudBorn[slot] = -1;
      hideSlot(this.sediment, 'aCloud', slot, SEDIMENT.grains);
    }
    for (let slot = 0; slot < this.groupBorn.length; slot++) {
      const born = this.groupBorn[slot] as number;
      if (born < 0 || this.age(born) <= BUBBLE_LIFE_S) continue;
      this.groupBorn[slot] = -1;
      hideSlot(this.bubbles, 'aStart', slot, BUBBLES.count[1]);
    }
  }

  /** How long ago, on the clock, something was sent at `born`. */
  private age(born: number): number {
    return (((this.clock - born) % PERIOD_S) + PERIOD_S) % PERIOD_S;
  }

  /** The clouds and the bubbles' groups not yet gone, for the tests. */
  alive(): { clouds: number; groups: number } {
    return { clouds: this.cloudBorn.filter((b) => b >= 0).length, groups: this.groupBorn.filter((b) => b >= 0).length };
  }

  /** The device's share lowered while its frames run slower than `slowS` for a second, raised again after five quicker than `quickS`. */
  private adapt(seconds: number): void {
    const a = PARTICLES.adapt;
    const most = PARTICLES.quality[this.settings.quality] * this.settings.density;
    if (!(seconds > 0) || seconds >= 0.25) return;
    this.slowFor = seconds > a.slowS ? this.slowFor + seconds : 0;
    this.quickFor = seconds < a.quickS ? this.quickFor + seconds : 0;
    if (this.slowFor > 1) this.share = Math.max(a.fewest * most, this.share * (1 - a.perS * seconds));
    else if (this.quickFor > 5) this.share = Math.min(most, this.share * (1 + a.perS * seconds));
  }

  /** The device's share of the particles now, for the tests. */
  shareNow(): number {
    return this.share;
  }

  /** How many suspended particles are asked for now, and how many shown (eased toward it), for the tests. */
  count(): number {
    return Math.round(this.wanted());
  }

  shownNow(): number {
    return this.shown;
  }

  /**
   * A parrotfish's bite on the reef at a floor point: a cloud there one time in `perBite`'s (by its own hash, the bite's
   * `key`), if the camera is within `withinM` of it.
   */
  bite(x: number, y: number, z: number, key: number, camera: THREE.Camera): boolean {
    const s = SEDIMENT;
    if (!this.settings.on.sediment || hash3(this.seed, key, 1_761) >= s.perBite) return false;
    if (camera.position.distanceToSquared(this.at.set(x, y, z)) > s.withinM ** 2) return false;
    const cloud = this.nextCloud;
    this.nextCloud = (this.nextCloud + 1) % s.clouds;
    this.cloudBorn[cloud] = this.clock;
    const at = this.sediment.geometry.getAttribute('aCloud') as THREE.BufferAttribute;
    const grain = this.sediment.geometry.getAttribute('aGrain') as THREE.BufferAttribute;
    for (let g = 0; g < s.grains; g++) {
      const i = cloud * s.grains + g;
      const h = (k: number): number => hash3(key, g, 1_762 + k);
      const angle = 2 * Math.PI * h(0);
      const out = s.spreadM[0] + (s.spreadM[1] - s.spreadM[0]) * Math.sqrt(h(1));
      at.setXYZW(i, x, y, z, this.clock);
      grain.setXYZW(i, Math.cos(angle) * out, s.riseM[0] + (s.riseM[1] - s.riseM[0]) * h(2), Math.sin(angle) * out, h(3) < s.fine ? 0 : 1);
    }
    for (const a of [at, grain]) {
      a.addUpdateRange(cloud * s.grains * 4, s.grains * 4);
      a.needsUpdate = true;
    }
    this.emitted++;
    return true;
  }

  /** Clouds sent so far, for the tests. */
  clouds(): number {
    return this.emitted;
  }

  /**
   * A group of bubbles one time in `everyS`'s seconds on the mean, `withinM` before the camera and `depthM` under the
   * surface, where the camera sees that point (none sent where it would not be seen).
   */
  private sendBubbles(camera: THREE.Camera, run: number): void {
    const b = BUBBLES;
    this.sinceGroup += run;
    const every = b.everyS / Math.max(1e-6, b.frequency);
    // A second's chance spread over its tenths, each drawn once, however many a frame holds, so it is the same at any
    // frame rate (M173's review: one a frame, fewer below ten frames a second).
    let sent = false;
    while (this.sinceGroup >= 0.1) {
      this.sinceGroup -= 0.1;
      this.ticks++;
      if (!sent && hash3(this.seed, this.ticks, 1_780) < 0.1 / every) sent = this.sendGroup(camera, this.ticks);
    }
  }

  /** A group sent from a spot before the camera drawn by `tick`, where the camera sees it; whether it was. */
  private sendGroup(camera: THREE.Camera, tick: number): boolean {
    const b = BUBBLES;
    const draw = (k: number): number => hash3(this.seed, tick, 1_780 + k);
    const forward = camera.getWorldDirection(this.at);
    forward.y = 0;
    if (forward.lengthSq() < 1e-9) return false;
    forward.normalize();
    const side = new THREE.Vector3(-forward.z, 0, forward.x);
    const away = b.withinM[0] + (b.withinM[1] - b.withinM[0]) * draw(1);
    const at = camera.position.clone().addScaledVector(forward, away).addScaledVector(side, (draw(2) - 0.5) * away);
    at.y = -(b.depthM[0] + (b.depthM[1] - b.depthM[0]) * draw(3));
    const seen = at.clone().project(camera);
    if (Math.abs(seen.x) > 0.95 || Math.abs(seen.y) > 0.95 || seen.z > 1) return false;
    this.bubbleGroup(at, tick);
    return true;
  }

  /** A group of bubbles sent up from a point, its draws by `key`. */
  bubbleGroup(at: THREE.Vector3, key: number): void {
    const b = BUBBLES;
    const group = this.nextGroup;
    this.nextGroup = (this.nextGroup + 1) % b.groups;
    this.groupBorn[group] = this.clock;
    const start = this.bubbles.geometry.getAttribute('aStart') as THREE.BufferAttribute;
    const bubble = this.bubbles.geometry.getAttribute('aBubble') as THREE.BufferAttribute;
    const count = b.count[0] + Math.floor((b.count[1] - b.count[0] + 1) * hash3(this.seed, key, 1_784));
    for (let k = 0; k < b.count[1]; k++) {
      const i = group * b.count[1] + k;
      if (k >= count) {
        start.setXYZW(i, 0, 0, 0, -1);
        continue;
      }
      const h = (j: number): number => hash3(key, k, 1_790 + j);
      const mm = b.diameterMm[0] + (b.diameterMm[1] - b.diameterMm[0]) * h(0) ** 2;
      // A little after one another, up to a second.
      start.setXYZW(i, at.x + (h(1) - 0.5) * b.spreadM, at.y - h(2) * b.spreadM, at.z + (h(3) - 0.5) * b.spreadM, (((this.clock - h(4)) % PERIOD_S) + PERIOD_S) % PERIOD_S);
      bubble.setXYZW(i, mm, along(b.riseMPerS, mm), h(5), 0.8 + 0.8 * h(6));
    }
    for (const a of [start, bubble]) {
      a.addUpdateRange(group * b.count[1] * 4, b.count[1] * 4);
      a.needsUpdate = true;
    }
    this.sentGroups++;
  }

  /** Bubbles' groups sent so far, for the tests. */
  groupsSent(): number {
    return this.sentGroups;
  }

  /** Each frame before it is drawn: the canvas's pixels a meter away and the device's pixels to one of the page's. */
  prepare(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera): void {
    renderer.getDrawingBufferSize(this.buffer);
    const ratio = renderer.getPixelRatio();
    const perM = ((this.buffer.y / ratio) * camera.projectionMatrix.elements[5]) / 2;
    for (const u of this.uniforms) {
      (u.uPxPerM as THREE.IUniform).value = perM;
      (u.uPixelRatio as THREE.IUniform).value = ratio;
    }
  }

  dispose(): void {
    for (const points of [this.suspended, this.sediment, this.bubbles]) {
      points.geometry.dispose();
      (points.material as THREE.Material).dispose();
    }
  }
}
